import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { CarouselProvider, Slider, Slide, DotGroup } from 'pure-react-carousel';
import { map, keys, upperFirst } from 'lodash';

import Row from '~/layout/Row';
import Column from '~/layout/Column';

import Button from '~/components/base/Button';
import Image from '~/components/base/Image';

import { useLocationPickerContext } from '~/context/LocationPickerContext';

import SocialLinks from '~/components/SocialLinks';
import IllustrationsBackground from '~/components/wrappers/IllustrationsBackground';

import { urlEndcodeAddress } from '~/utilities/locations';
import { dynamicFontSize, formatTime } from '~/utilities/helpers';

import { h1, p } from '~/styles/typography';
import { calcVw } from '~/styles/helpers';

const Wrapper = styled.div`
    position: relative;
    padding-bottom: 65px;
    margin-bottom: 40px;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 50%;
        right: 50%;
        margin: 0 ${calcVw(-50)};
        width: ${calcVw(100)};
        height: 100%;
        background-color: ${({ theme }) => theme.colors.text.lightBlue};
        z-index: ${({ theme }) => theme.zIndex.backgroundColor};
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
        margin-bottom: 120px;
    }
`;

const H1 = styled.h1`
    ${h1};
    text-align: center;
    margin-top: 30px;
    color: ${({ theme }) => theme.colors.accent.blue};

    @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
        margin-top: 120px;
        text-align: left;
    }
`;

const StyledColumn = styled(Column)`
    @media (max-width: ${({ theme }) =>
            theme.breakpointNumbers.largeTablet - 1}px) {
        order: 2;
    }
`;

const ButtonsWrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 15px 10px;
    margin-top: 35px;
`;

const StyledButton = styled(Button)`
    flex: 1 1 100%;
    justify-content: center;
    display: none;
    ${({ show }) => show && 'display: inline-flex'};

    @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
        flex: 0 1 auto;
    }
`;

const LocationAndHours = styled.div`
    display: grid;
    grid-template-areas:
        'location'
        'hours'
        'social';
    grid-template-rows: auto auto auto;
    gap: 35px;
    margin-top: 35px;

    @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
        margin-top: 45px;
        grid-template-areas:
            'location hours'
            'social hours';
        grid-template-rows: 1fr 1fr;
        grid-template-columns: 1fr 1fr;
    }
`;

const LocationAddress = styled.div`
    grid-area: location;
`;

const Social = styled.div`
    grid-area: social;
    display: flex;
    justify-content: center;

    @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
        justify-content: flex-start;
    }
`;

const Hours = styled.div`
    grid-area: hours;
`;

const H2 = styled.h2`
    font-family: ${({ theme }) => theme.fonts.heading};
    font-weight: ${({ theme }) => theme.fontWeights.black};
    font-size: ${dynamicFontSize(1.6, 2.0, 2.6)};
    color: ${({ theme }) => theme.colors.accent.blue};
    text-transform: uppercase;
    margin-bottom: 20px;
`;

const P = styled.p`
    ${p};
    line-height: 1.45;
    font-weight: ${({ theme }) => theme.fontWeights.medium};
`;

const Address = styled.address`
    ${p};
    line-height: 1.45;
    font-weight: ${({ theme }) => theme.fontWeights.medium};
`;

const PhoneLink = styled.a`
    ${p};
    line-height: 1.45;
    font-weight: ${({ theme }) => theme.fontWeights.medium};

    &:hover,
    &:focus {
        text-decoration: underline;
    }
`;

const CarouselWrapper = styled.div`
    position: relative;
    width: ${calcVw(100)};
    left: 50%;
    right: 50%;
    margin: 0 ${calcVw(-50)};

    @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
        left: auto;
        right: auto;
        margin: 120px 0 0 0;
        width: calc(
            ${calcVw(50)} - ${({ theme }) => theme.layout.gridGutter} / 2
        );
    }

    .carousel {
        width: 100%;
    }
`;

const StyledImage = styled(Image)`
    width: 100%;
`;

const StyledDotGroup = styled(DotGroup)`
    width: 100%;
    height: 78px;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 40px;
    display: none;

    .carousel__dot {
        height: 18px;
        width: 18px;
        background-color: ${({ theme }) => theme.colors.primary.yellow};
        border-radius: 50%;
        position: relative;

        &.carousel__dot--selected {
            &::before {
                content: '';
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                width: 32px;
                height: 32px;
                border-radius: 50%;
                background-color: ${({ theme }) => theme.colors.primary.red};
                z-index: ${({ theme }) => theme.zIndex.backgroundColor};
            }

            &::after {
                content: '';
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                width: 25px;
                height: 25px;
                border-radius: 50%;
                background-color: ${({ theme }) => theme.colors.text.lightBlue};
                z-index: ${({ theme }) => theme.zIndex.backgroundColor};
            }
        }
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
        display: flex;
    }
`;

const LocationHeader = ({
    locationID,
    address,
    mainPhone,
    hours,
    facebookPageUrl,
    twitterHandle,
    instagramHandle,
    photos,
    comingSoon
}) => {
    const { setLocation, selectedLocation } = useLocationPickerContext();

    const socialAccounts = [];
    if (facebookPageUrl) {
        socialAccounts.push({
            linkUrl: facebookPageUrl,
            iconClass: 'facebook'
        });
    }
    if (twitterHandle) {
        socialAccounts.push({
            linkUrl: `https://twitter.com/${twitterHandle}`,
            iconClass: 'x'
        });
    }
    if (instagramHandle) {
        socialAccounts.push({
            linkUrl: `https://www.instagram.com/${instagramHandle}`,
            iconClass: 'instagram'
        });
    }

    return (
        <Wrapper>
            <IllustrationsBackground backgroundGraphic="singleLocation" />
            <Row>
                <StyledColumn span={12} spanLargeTablet={6}>
                    <H1>{address.line1}</H1>
                    <ButtonsWrapper>
                        <StyledButton
                            show={true}
                            href={`https://www.google.com/maps/dir/?api=1&destination=${urlEndcodeAddress(address)}`}
                        >
                            Get Directions
                        </StyledButton>
                        {!comingSoon && (
                            <StyledButton
                                onClick={() => setLocation(locationID)}
                                show={locationID !== selectedLocation?.id}
                            >
                                Make this My Autobell
                            </StyledButton>
                        )}
                    </ButtonsWrapper>
                    <LocationAndHours>
                        <LocationAddress>
                            <H2>Location</H2>
                            <Address>
                                {address.line1}
                                <br />
                                {address.city}, {address.region}{' '}
                                {address.postalCode}
                                <br />
                                <PhoneLink href={`tel:${mainPhone}`}>
                                    {mainPhone}
                                </PhoneLink>
                            </Address>
                        </LocationAddress>
                        <Hours>
                            <H2>Hours</H2>
                            {map(keys(hours), (day) =>
                                hours[day] ? (
                                    <P key={day}>
                                        <span>{upperFirst(day)}:</span>{' '}
                                        {formatTime(
                                            hours[day].openIntervals[0].start
                                        )}{' '}
                                        -{' '}
                                        {formatTime(
                                            hours[day].openIntervals[0].end
                                        )}
                                    </P>
                                ) : null
                            )}
                        </Hours>
                        <Social>
                            <SocialLinks
                                {...{ socialAccounts }}
                                color="blue"
                                size={41}
                                sizeLarge={41}
                            />
                        </Social>
                    </LocationAndHours>
                </StyledColumn>
                <Column span={12} spanLargeTablet={6}>
                    {photos?.length && (
                        <CarouselWrapper>
                            <CarouselProvider
                                naturalSlideWidth={952}
                                naturalSlideHeight={528}
                                totalSlides={photos.length}
                            >
                                <Slider>
                                    {map(photos, ({ image }, i) => (
                                        <Slide key={image.url} index={i}>
                                            <StyledImage
                                                sourceUrl={image.url}
                                            />
                                        </Slide>
                                    ))}
                                </Slider>
                                {photos.length > 1 && <StyledDotGroup />}
                            </CarouselProvider>
                        </CarouselWrapper>
                    )}
                </Column>
            </Row>
        </Wrapper>
    );
};

LocationHeader.propTypes = {
    locationID: PropTypes.string.isRequired,
    address: PropTypes.shape({
        line1: PropTypes.string.isRequired,
        city: PropTypes.string.isRequired,
        region: PropTypes.string.isRequired,
        postalCode: PropTypes.string.isRequired
    }).isRequired,
    mainPhone: PropTypes.string.isRequired,
    hours: PropTypes.object.isRequired,
    facebookPageUrl: PropTypes.string,
    twitterHandle: PropTypes.string,
    instagramHandle: PropTypes.string,
    photos: PropTypes.arrayOf(
        PropTypes.shape({
            image: PropTypes.shape({
                url: PropTypes.string.isRequired
            })
        })
    ),
    comingSoon: PropTypes.bool
};

export default LocationHeader;
